// Road service status history — the dispatch board's half.
//
// The transition payloads themselves come from the shared domain layer, the
// same ones the native app builds. This module is only the wire: it knows how
// to talk to AppSync and nothing about what a transition means.

import { generateClient } from 'aws-amplify/api';
import {
  buildRoadCallPatch,
  buildRoadStatusEvent,
} from '../../shared/domain';
import { DEFAULT_LOCATION_ID, nowSeconds } from '../events/operationEvents';

const client = generateClient();

export const ROAD_DISPATCH_SOURCE = 'web-dispatch';

// Inline documents: codegen regenerates queries.js/mutations.js on the next
// `amplify push`, and the board must not be broken while waiting for it.
const CREATE_STATUS_EVENT = `
  mutation CreateRoadServiceStatusEvent($input: CreateRoadServiceStatusEventInput!) {
    createRoadServiceStatusEvent(input: $input) { id }
  }
`;

const EVENTS_BY_CALL = `
  query RoadServiceStatusEventsByCall($roadServiceCallId: ID!) {
    roadServiceStatusEventsByCall(
      roadServiceCallId: $roadServiceCallId
      sortDirection: ASC
      limit: 200
    ) {
      items {
        id roadServiceCallId locationId status fromStatus at
        technicianId techNumber techName holdReason holdNote resumeStatus source
      }
    }
  }
`;

function statusEventId() {
  return `rse-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The call patch and the history row for one transition, built together so they
 * can't disagree about what just happened.
 */
export function buildDispatchTransition({ call, next, technician, holdReason, holdNote, events = [] }) {
  const at = nowSeconds();
  return {
    at,
    callPatch: buildRoadCallPatch({
      call,
      next,
      at,
      technicianId: technician?.id || null,
      holdReason,
      holdNote,
    }),
    statusEvent: buildRoadStatusEvent({
      call,
      next,
      at,
      id: statusEventId(),
      technicianId: technician?.id || null,
      techNumber: technician?.number || null,
      techName: technician?.name || null,
      holdReason,
      holdNote,
      events,
      source: ROAD_DISPATCH_SOURCE,
      defaultLocationId: DEFAULT_LOCATION_ID,
    }),
  };
}

/**
 * Write the history row. Never let this fail the transition itself — a board
 * that refuses to move a call because the audit row didn't save is worse than
 * a gap in the audit.
 */
export async function writeRoadStatusEvent(input) {
  try {
    await client.graphql({ query: CREATE_STATUS_EVENT, variables: { input } });
    return true;
  } catch (err) {
    console.warn('road status event write failed:', err?.errors?.[0]?.message || err.message);
    return false;
  }
}

export async function fetchRoadStatusEvents(callId) {
  if (!callId) return [];
  try {
    const result = await client.graphql({
      query: EVENTS_BY_CALL,
      variables: { roadServiceCallId: callId },
    });
    return result.data?.roadServiceStatusEventsByCall?.items || [];
  } catch (err) {
    console.warn('road status event fetch failed:', err?.errors?.[0]?.message || err.message);
    return [];
  }
}
